import React, { createContext, useState, useContext, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { updateConsoleForUser } from '@/utils/consoleControl';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    checkUserAuth();
  }, []);

  const checkUserAuth = async () => {
    try {
      setIsLoadingAuth(true);
      setAuthError(null);
      
      // Check if we have a token
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setIsLoadingAuth(false);
        setIsAuthenticated(false);
        return;
      }

      // Verify token and get current user
      const currentUser = await api.auth.me();
      setUser(currentUser);
      setIsAuthenticated(true);
      
      // Update console logging based on user
      if (currentUser?.email) {
        updateConsoleForUser(currentUser.email);
      }
      
      setIsLoadingAuth(false);
    } catch (error) {
      console.error('User auth check failed:', error);
      setIsLoadingAuth(false);
      setIsAuthenticated(false);
      
      // If user auth fails, it might be an expired token
      if (error.status === 401 || error.status === 403) {
        setAuthError({
          type: 'auth_required',
          message: 'Authentication required'
        });
      }
    }
  };

  const login = async (email, password) => {
    try {
      const user = await api.auth.login(email, password);
      setUser(user);
      setIsAuthenticated(true);
      setAuthError(null);
      
      // Update console logging based on user
      if (user?.email) {
        updateConsoleForUser(user.email);
      }
      
      return user;
    } catch (error) {
      console.error('Login failed:', error);
      setAuthError({
        type: 'login_failed',
        message: error.response?.data?.error || 'Login failed'
      });
      throw error;
    }
  };

  const register = async (email, password, full_name, user_role) => {
    try {
      const user = await api.auth.register(email, password, full_name, user_role);
      setUser(user);
      setIsAuthenticated(true);
      setAuthError(null);
      
      // Update console logging based on user
      if (user?.email) {
        updateConsoleForUser(user.email);
      }
      
      return user;
    } catch (error) {
      console.error('Registration failed:', error);
      setAuthError({
        type: 'registration_failed',
        message: error.response?.data?.error || 'Registration failed'
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      // Disable console on logout
      updateConsoleForUser(null);
    }
  };

  const updateUser = async (data) => {
    try {
      const updatedUser = await api.auth.updateMe(data);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Update user failed:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoadingAuth,
      authError,
      login,
      register,
      logout,
      updateUser,
      checkUserAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
